import {
  AppShell as MantineAppShell, Box, Burger, Button, Container, Group, NavLink,
  Stack, Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBuildingBank, IconCalendarTime, IconChecklist, IconClipboardList,
  IconHelp, IconHistory, IconLogout,
  IconScale, IconSettings, IconShieldCheck, IconUserCog, IconUsers,
} from '@tabler/icons-react';
import { createPortal } from 'react-dom';
import { Link, Outlet, NavLink as RouterNavLink } from 'react-router-dom';

import { useCurrentUser, useLogout } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useDirtyForm } from './DirtyFormProvider';
import { BaluarteLogo } from './BaluarteLogo';
import { GlobalSearch } from './GlobalSearch';
import { DlpProtection } from '../dlp/DlpProtection';
import { Watermark } from '../dlp/Watermark';
import type { UserRole } from '../api/types';
import type { PermissionKey } from '../api/permissionKeys';

const NAVBAR_WIDTH = 240;
const HEADER_HEIGHT = 135;
const LOGO_HEIGHT = 113;

// Role-aware sidebar visibility.
//
// Each NAV_ITEM has an optional `roles` whitelist. If absent the item is
// visible to every authenticated user. The day-to-day RM destinations
// (Agreements / Persons / Accounts / Insurance Policies / Policy parties)
// have no whitelist so they remain visible to all roles including
// read_only auditors. The compliance/admin tail uses explicit role lists.
//
// Server-side, write actions are also gated by require_writer / require_admin
// / require_compliance dependencies — the sidebar filter is just UX hygiene.
type NavItem = {
  to: string;
  label: string;
  icon: typeof IconClipboardList;
  roles?: ReadonlyArray<UserRole>;
  // #14 P3: gate on an effective permission instead of a role whitelist.
  permission?: PermissionKey;
};

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  // Day-to-day destinations — visible to all roles.
  { to: '/agreements',         label: 'Agreements',          icon: IconClipboardList },
  { to: '/persons',            label: 'Persons',             icon: IconUsers         },
  { to: '/accounts',           label: 'Accounts',            icon: IconBuildingBank  },
  { to: '/account-insurance',  label: 'Insurance Policies',  icon: IconShieldCheck   },
  { to: '/insurance-policy-parties', label: 'Policy parties', icon: IconUserCog       },
  // Feature 2 (2026-05-28): per-user filing tracker. Visible only to roles
  // whose writes route through the maker-checker queue (RM + staff) — admin
  // and compliance self-apply, so their "My" page would be redundant with
  // the audit log. See MyChangeRequestsPage.tsx for the design rationale.
  { to: '/my-change-requests', label: 'My Change Requests', icon: IconHistory,
    roles: ['rm', 'staff'] },
  // Feature 4 (2026-05-28): per-user task tracker. For makers it's their
  // creator-or-assignee list. For admin (added below) it's "All Tasks"
  // across the firm — the page label flips role-aware in MyTasksPage.
  { to: '/my-tasks',           label: 'My Tasks',           icon: IconChecklist,
    roles: ['rm', 'staff'] },
  { to: '/my-tasks',           label: 'All Tasks',          icon: IconChecklist,
    roles: ['admin'] },
  // C-356 (Pedro 2026-10-11): the RM's own Compliance worklist — expiring dates and
  // incomplete records on the agreements where they are a Named RM. Admin and Compliance
  // use the Compliance page (firm-wide) instead.
  { to: '/my-follow-ups',      label: 'My follow-ups',      icon: IconCalendarTime,
    roles: ['rm', 'staff', 'read_only'] },
  // Admin back-office hub (2026-06-21 nav reorg): Portfolio Data (20th / EOM),
  // Users, Permissions, SAA reference, Standard/Compute fees, Translations.
  // Admin-only; a future Permissions affordance will let non-admins be granted
  // visibility per-user (notably to give Compliance back its SAA/fees/translations
  // nav path). Individual pages keep their own per-card gates.
  { to: '/admin',              label: 'Admin',               icon: IconSettings,
    roles: ['admin'] },
  // Compliance — review queue, completeness, review cadences, audit log + data
  // extraction. (The Users/Permissions/SAA/Fees cards moved to Admin 2026-06-21;
  // Audit log + Data extraction stay here.) Admin and Compliance only (C-356, Pedro
  // 2026-10-11; read_only lost it then) — the page carries the same gate
  // (CompliancePage COMPLIANCE_PAGE_ROLES; scripts/check-nav-gates.mjs keeps them aligned).
  { to: '/compliance',         label: 'Compliance',          icon: IconScale,
    roles: ['admin', 'compliance'] },
];

export function AppShell() {
  const { user } = useCurrentUser();
  const { can } = usePermissions();
  const logout = useLogout();
  const { confirmDiscardAsync, anyDirty } = useDirtyForm();
  const [opened, { toggle }] = useDisclosure();

  // Task #251 Stage 2 (2026-06-04): the previous `guardedNavigate` here
  // existed to intercept dirty-form sidebar/logo navigation BEFORE
  // useBlocker was usable. With the DataRouter migration + useBlocker
  // wired into useDirtyFormGuard, in-app router navigation is now
  // intercepted at the page level — keeping `guardedNavigate` would
  // cause a double-prompt (AppShell modal first, then useBlocker modal
  // when AppShell's navigate(to) fires). NavLinks and the logo Link
  // now navigate naturally; useBlocker handles dirty-form confirmation.
  //
  // The logout guard stays because logout doesn't go through React
  // Router navigation — it calls logout.mutate() which clears the
  // session and triggers a redirect via the auth state, NOT a
  // router.navigate(). useBlocker can't intercept that, so we keep
  // the explicit anyDirty()+confirmDiscardAsync() check here.
  function guardedLogout() {
    if (!anyDirty()) { logout.mutate(); return; }
    void confirmDiscardAsync().then((ok) => { if (ok) logout.mutate(); });
  }

  function isVisibleTo(item: NavItem): boolean {
    if (item.permission) return can(item.permission);
    if (!item.roles) return true;
    if (!user) return false;
    return item.roles.includes(user.role);
  }

  return (
    <MantineAppShell
      header={{ height: HEADER_HEIGHT }}
      navbar={{ width: NAVBAR_WIDTH, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <MantineAppShell.Header>
        {/*
          The header is a 3-zone layout:
            (1) logo block, fixed NAVBAR_WIDTH on the left;
            (2) a flex-1 zone that mirrors AppShell.Main + Container so the
                global search box's left edge tracks the per-page search
                box at every viewport width (both Container size="xl" with
                padding-inline="md", inside an AppShell.Main padding="md");
            (3) the user-info / Sign out group, absolute-positioned to the
                right edge of the header so it stays anchored to the
                viewport regardless of the centered content column width.
        */}
        <Group h="100%" gap={0} wrap="nowrap" align="center">
          <Group h="100%" px="md" w={NAVBAR_WIDTH} style={{ flexShrink: 0 }} wrap="nowrap" justify="center">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            {/* Clickable logo — always returns to /, even from a nested
                detail page. Uses Link for keyboard / middle-click semantics.
                Dirty-form prompting is now handled by useBlocker (Task #251
                Stage 2) so this Link navigates naturally. */}
            <Link to="/"
              style={{ display: 'inline-flex', alignItems: 'center' }}
              aria-label="Go to dashboard">
              <BaluarteLogo variant="horizontal" height={LOGO_HEIGHT} />
            </Link>
          </Group>
          <Box h="100%" px="md" style={{ flex: 1, minWidth: 0 }}>
            <Container size="xl" h="100%" px="md">
              <Group h="100%" align="center" wrap="nowrap">
                <GlobalSearch />
              </Group>
            </Container>
          </Box>
        </Group>
        <Group
          h="100%"
          px="md"
          gap="md"
          wrap="nowrap"
          align="center"
          style={{ position: 'absolute', top: 0, right: 0 }}
        >
          <Text size="sm" c="dimmed">{user?.display_name} ({user?.role})</Text>
          <Button variant="subtle" size="xs" leftSection={<IconLogout size={14} />}
            onClick={guardedLogout} loading={logout.isPending}>Sign out</Button>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p="xs">
        {/* #15 (2026-06-17): nav items at the top, Help pinned at the bottom
            via a full-height space-between column. */}
        <Stack gap="xs" h="100%" justify="space-between">
          <Stack gap="xs">
            {NAV_ITEMS
              .filter(isVisibleTo)
              .map(({ to, label, icon: Icon }) => (
                <NavLink key={to} component={RouterNavLink} to={to}
                  label={label} leftSection={<Icon size={16} />}
                />
              ))}
          </Stack>
          <NavLink component={RouterNavLink} to="/help"
            label="Help" leftSection={<IconHelp size={16} />} />
        </Stack>
      </MantineAppShell.Navbar>

      <MantineAppShell.Main>
        <Outlet />
      </MantineAppShell.Main>

      {/*
        DLP layer (#117) — only mounted inside the authenticated shell so
        unauthenticated /login is unaffected.
          - DlpProtection: print gate, blur surveillance, key intercept,
            context-menu suppression. Owns the print modal.
          - Watermark: SVG-tiled overlay carrying user.email + UTC stamp.
          - #__baluarte-print-notice: hidden on screen; revealed by the
            @media print CSS in dlp/dlp.css to replace the document body
            during print preview / Save as PDF.
      */}
      <DlpProtection />
      <Watermark />
      {/* Print-notice rendered via portal directly into <body> so the
          @media print selector `body > *:not(#__baluarte-print-notice)`
          can hide siblings (the React app, Mantine portals, etc.)
          without hiding the notice itself. */}
      {createPortal(
        <div id="__baluarte-print-notice" style={{ display: 'none' }}>
          <div className="title">Restricted content</div>
          <div className="body">
            This page contains regulated client data and may not be printed
            or saved to PDF. The original page has been suppressed in this
            print output. Your user and the print event have been logged in
            the Baluarte CRM audit trail.
          </div>
          <div className="body">
            If you have a legitimate business need to extract data, ask an
            administrator to use the audit-CSV export.
          </div>
          <div className="meta">
            {user?.email} · {new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC
          </div>
        </div>,
        document.body,
      )}
    </MantineAppShell>
  );
}
